import { FunctionalComponent, h } from 'preact';
import './move-hide.scss';
import { GearIcon } from '@uw/icons';
import { useContext, useRef, useState } from 'preact/hooks';
import { useTranslation } from 'hooks';
import { ApplicationContext } from 'contexts/use-application-setup';
import { layoutStore } from 'global/layout.store';
import { SR_ANNOUNCEMENT_ELEMENT_ID } from 'constants/constants';
import { AllyFeaturesContext } from '../accessibility-features/use-ally-features';
import { HideWidget } from './hide-widget';
import { usePosition } from './use-position';
import { ExpandPanel } from '../expand-panel/expand-panel';
import { SCROLLED_TO_HIDE_OPTIONS_TIMEOUT } from './constants';
// values according to the old data-grid positioning
enum Position {
  Left = 7,
  Center = 5,
  Right = 1,
}

export const MoveHide: FunctionalComponent = () => {
  const { translate } = useTranslation();
  const { tunings, accountIdCode } = useContext(ApplicationContext);
  const { allyFeaturesList } = useContext(AllyFeaturesContext);
  const [isHideWidgetVisible, setIsHideWidgetVisible] = useState(false);
  const announcementTextRef = useRef<HTMLDivElement>(null);
  const hideOptionsContainerRef = useRef<HTMLDivElement>(null);
  const { currentPosition, savePosition } = usePosition(() => setIsHideWidgetVisible(false));
  const isHideAvailable = !tunings.widget_custom_trigger_enabled;
  const translationForMoveOnly = isHideAvailable ? '' : '.onlyMove';

  // disable move/hide for specific customer
  const isMoveHideDisabled = accountIdCode === atob('aVVSaVVMZm1uZQ==');

  if (isMoveHideDisabled || !allyFeaturesList.length) {
    return null;
  }

  const handleHide = () => {
    setIsHideWidgetVisible(true);
    requestAnimationFrame(() => {
      // VoiceOver with a certain setting blocks the scroll to the bottom of the page
      setTimeout(() => {
        hideOptionsContainerRef.current &&
          layoutStore.makeSureElementVisible(hideOptionsContainerRef.current);
        // need to set the text content to trigger aria-live
        announcementTextRef.current &&
          (announcementTextRef.current.textContent = translate(
            'widget.manageHideMove.hide_options.announce_options',
          ));
      }, SCROLLED_TO_HIDE_OPTIONS_TIMEOUT);
    });
  };

  return (
    <div className="uwaw-options">
      <div className="uwaw-options__item">
        <div
          className="uwaw-move-hide-component"
          data-uw-reader-content={translate(`widget.menu.moveHide${translationForMoveOnly}.aria`)}
        >
          <ExpandPanel
            title={translate(`widget.manageHideMove${translationForMoveOnly}.title`)}
            icon={<GearIcon width={16} height={16} />}
            scrollDown={true}
            readerText={translate(`widget.menu.moveHide${translationForMoveOnly}.aria`)}
          >
            <div className="uwaw-move-hide">
              <ul className="uwaw-move-hide__list">
                <li className="uwaw-move-hide__item">
                  <label htmlFor="uwaw-move-l" className="uwaw-custom-radio">
                    <input
                      className="uwaw-custom-radio__inp"
                      type="radio"
                      name="moveHide"
                      id="uwaw-move-l"
                      aria-label={translate(
                        'widget.manageHideMove.radio_icons.icons_aria_labels.left',
                      )}
                      data-uw-reader-content={translate(
                        'widget.manageHideMove.radio_icons.icons_aria_labels.left',
                      )}
                      checked={currentPosition === Position.Left}
                      onInput={() => savePosition(Position.Left)}
                    />
                    <span className="uwaw-custom-radio__icon" />
                    <span className="uwaw-custom-radio__label">
                      {translate('widget.manageHideMove.radio_icons.left')}
                    </span>
                  </label>
                </li>

                <li className="uwaw-move-hide__item">
                  <label htmlFor="uwaw-move-r" className="uwaw-custom-radio">
                    <input
                      className="uwaw-custom-radio__inp"
                      type="radio"
                      name="moveHide"
                      id="uwaw-move-r"
                      aria-label={translate(
                        'widget.manageHideMove.radio_icons.icons_aria_labels.right',
                      )}
                      data-uw-reader-content={translate(
                        'widget.manageHideMove.radio_icons.icons_aria_labels.right',
                      )}
                      checked={currentPosition === Position.Right}
                      onInput={() => savePosition(Position.Right)}
                    />
                    <span className="uwaw-custom-radio__icon" />
                    <span className="uwaw-custom-radio__label">
                      {translate('widget.manageHideMove.radio_icons.right')}
                    </span>
                  </label>
                </li>

                {isHideAvailable && (
                  <li className="uwaw-move-hide__item">
                    <label htmlFor="uwaw-move-h" className="uwaw-custom-radio">
                      <input
                        className="uwaw-custom-radio__inp"
                        type="radio"
                        name="moveHide"
                        id="uwaw-move-h"
                        aria-label={translate(
                          'widget.manageHideMove.radio_icons.icons_aria_labels.hide',
                        )}
                        data-uw-reader-content={translate(
                          'widget.manageHideMove.radio_icons.icons_aria_labels.hide',
                        )}
                        checked={isHideWidgetVisible}
                        onInput={handleHide}
                      />
                      <span className="uwaw-custom-radio__icon" />
                      <span className="uwaw-custom-radio__label">
                        {translate('widget.manageHideMove.radio_icons.hide')}
                      </span>
                    </label>
                  </li>
                )}
              </ul>

              {isHideWidgetVisible && (
                <div
                  ref={announcementTextRef}
                  id={SR_ANNOUNCEMENT_ELEMENT_ID}
                  className="sr-only"
                  aria-live="polite"
                />
              )}

              {isHideAvailable && isHideWidgetVisible && (
                <HideWidget
                  ref={hideOptionsContainerRef}
                  onCancel={() => {
                    setIsHideWidgetVisible(false);
                  }}
                />
              )}
            </div>
          </ExpandPanel>
        </div>
      </div>
    </div>
  );
};
